import React, { Fragment, useContext } from 'react'
import { Switch, Route, useLocation } from 'react-router-dom'
import ScrollIntoViewIfNeeded from 'react-scroll-into-view-if-needed'
import Sidebar, { PrimaryNavLink, SecondaryNavLink } from '../layout/sidebar'
import { MenuContext } from '../layout/global_layout'
import { breakpoints } from '../theme'
import useWindowWidth from '../utils/useWindowWidth'

import ContentWrapper from '../layout/content_wrapper'

import Overview from './pages/overview'
import RateLimiting from './pages/rate_limiting'
import Authentication from './pages/authentication'
import JsonApi from './pages/json_api'
import DatesTimes from './pages/dates_times'
import FileUploads from './pages/file_uploads'
import Libraries from './pages/libraries'
import Versioning from './pages/versioning'
import Webhooks from './pages/webhooks'
import Help from './pages/help'
import Errors from './pages/errors'
import AddOns from './pages/add_ons'
import AddOns_BuildingYourFirstAddOn from './pages/add_ons/building_your_first_add_on'
import AddOns_AddOnsCLI from './pages/add_ons/add_ons_cli'
import AddOns_APIReference from './pages/add_ons/api_reference'
import AddOns_ComponentLibrary from './pages/add_ons/component_library'
import AddOns_Guidelines from './pages/add_ons/guidelines'
import AddOns_InsertionPoints from './pages/add_ons/insertion_points'
import AddOns_OAuth from './pages/add_ons/oauth'
import AddOns_Process from './pages/add_ons/process'
import AddOns_Terminology from './pages/add_ons/terminology'
import ITOS from './pages/itos'

const SidebarLinks = [
  { title: 'Getting Started', path: '/overview/' },
  { title: 'Authentication', path: '/overview/authentication' },
  { title: 'Rate Limiting', path: '/overview/rate-limiting' },
  { title: 'JSON-API', path: '/overview/json-api' },
  { title: 'Dates & Times', path: '/overview/dates-times' },
  { title: 'File Uploads', path: '/overview/file-uploads' },
  { title: 'Libraries', path: '/overview/libraries' },
  { title: 'Errors', path: '/overview/errors' },
  { title: 'Versioning', path: '/overview/versioning' },
  { title: 'Webhooks', path: '/overview/webhooks' },
  {
    title: 'Add-ons',
    path: '/overview/add-ons',
    secondaryLinks: [
      { title: 'Process', path: '/overview/add-ons/process' },
      { title: 'Terminology', path: '/overview/add-ons/terminology' },
      {
        title: 'Building Your First Add-On',
        path: '/overview/add-ons/building-your-first-add-on',
      },
      {
        title: 'Add-ons CLI',
        path: '/overview/add-ons/add-ons-cli',
      },
      {
        title: 'Insertion Points',
        path: '/overview/add-ons/insertion-points',
      },
      {
        title: 'Component Library',
        path: '/overview/add-ons/component-library',
      },
      { title: 'API Reference', path: '/overview/add-ons/api-reference' },
      { title: 'OAuth', path: '/overview/add-ons/oauth' },
      { title: 'Guidelines', path: '/overview/add-ons/guidelines' },
    ],
  },
  { title: 'Help', path: '/overview/help' },
]
export default function OverviewIndex() {
  const { setMobileMenuOpen } = useContext(MenuContext)
  const windowWidth = useWindowWidth()
  const location = useLocation()
  const { pathname } = location
  const isScrollReady = useIsScrollReady()

  const closeMobileMenu = () => {
    if (windowWidth < breakpoints.lg) {
      setMobileMenuOpen(false)
    } else {
      return false
    }
  }

  return (
    <Fragment>
      <Sidebar>
        {SidebarLinks.map(link => {
          const linkActive =
            pathname === link.path ||
            (link.path !== '/overview/' && pathname.startsWith(link.path))

          return (
            <Fragment key={link.title}>
              <PrimaryNavLink
                onClick={
                  link.secondaryLinks ? undefined : () => closeMobileMenu()
                }
                selected={linkActive}
                to={link.path}
              >
                {link.title}
              </PrimaryNavLink>

              {(link.secondaryLinks || []).map(sLink => {
                if (!linkActive) {
                  return null
                }

                return (
                  <div key={`${link.title}_${sLink.title}`}>
                    <ScrollIntoViewIfNeeded
                      options={{
                        behavior: 'instant',
                        scrollMode: 'always',
                      }}
                      active={isScrollReady && pathname === sLink.path}
                    >
                      <SecondaryNavLink
                        onClick={() => closeMobileMenu()}
                        selected={pathname === sLink.path}
                        to={sLink.path}
                      >
                        {sLink.title}
                      </SecondaryNavLink>
                    </ScrollIntoViewIfNeeded>
                  </div>
                )
              })}
            </Fragment>
          )
        })}
      </Sidebar>
      <ContentWrapper>
        <Switch>
          <Route
            path="/overview/add-ons/api-reference"
            component={AddOns_APIReference}
          />
          <Route
            path="/overview/add-ons/add-ons-cli"
            component={AddOns_AddOnsCLI}
          />
          <Route
            path="/overview/add-ons/building-your-first-add-on"
            component={AddOns_BuildingYourFirstAddOn}
          />
          <Route
            path="/overview/add-ons/component-library"
            component={AddOns_ComponentLibrary}
          />
          <Route
            path="/overview/add-ons/guidelines"
            component={AddOns_Guidelines}
          />
          <Route path="/overview/add-ons/insertion-points" component={AddOns_InsertionPoints} />
          <Route path="/overview/add-ons/oauth" component={AddOns_OAuth} />
          <Route path="/overview/add-ons/process" component={AddOns_Process} />
          <Route
            path="/overview/add-ons/terminology"
            component={AddOns_Terminology}
          />
          <Route path="/overview/add-ons" component={AddOns} />

          <Route path="/overview/rate-limiting" component={RateLimiting} />
          <Route path="/overview/authentication" component={Authentication} />
          <Route path="/overview/json-api" component={JsonApi} />
          <Route path="/overview/dates-times" component={DatesTimes} />
          <Route path="/overview/file-uploads" component={FileUploads} />
          <Route path="/overview/libraries" component={Libraries} />
          <Route path="/overview/errors" component={Errors} />
          <Route path="/overview/versioning" component={Versioning} />
          <Route path="/overview/webhooks" component={Webhooks} />
          <Route path="/overview/help" component={Help} />
          <Route path="/overview/itos" component={ITOS} />

          <Route path="/overview" component={Overview} />
        </Switch>
      </ContentWrapper>
    </Fragment>
  )
}

function useIsScrollReady() {
  const [ready, setReady] = React.useState(false)
  React.useEffect(() => setReady(true), [])

  return ready
}
